import { useEffect, useMemo, useState } from 'react'
import { CompoundLabel, CompoundTextInput, CompoundValue, StyledSelect } from 'components/RXTable'
import type { Option } from 'components/Select'
import { useFormData } from 'hooks/useFormData'
import moment from 'moment'
import { type Prescriber } from 'types'
import { normalizeProviderType, validateNpi } from 'utils/prescriber'
import { ListRowLabel, NpiTextInput, RXDataRow } from './StyledComponents'

const options: Option<'F' | 'M'>[] = [
  { label: 'Female', value: 'F' },
  { label: 'Male', value: 'M' },
]

const findGenderOption = (gender: string | undefined): Option<'F' | 'M'> | undefined => {
  if (!gender) return undefined
  return options.find(option => option.value === gender)
}

type DemographicBlockType = {
  prescriber: Prescriber
  isVet: boolean
}

const getSuffix = (prescriber: Prescriber) => {
  if (prescriber.suffix) {
    return `, ${prescriber.suffix}`
  } else if (prescriber?.npiDoc?.credential) {
    const credential: string = prescriber.npiDoc.credential
    return `, ${normalizeProviderType(credential)}`
  }
  return ''
}

const EditDemographicBlock = ({ prescriber, isVet }: DemographicBlockType): JSX.Element => {
  const [isValidNpi, setIsValidNpi] = useState(false)

  const {
    state: { formData },
    actions: { updateFormData },
  } = useFormData()

  const prescriberFormData = formData.prescriber as Prescriber

  useEffect(() => {
    updateFormData({
      prescriber: {
        $set: {
          ...prescriber,
          suffix: prescriber.suffix ?? prescriber.npiDoc?.credential,
          gender: prescriber.gender ?? prescriber.npiDoc?.gender,
        },
      },
    })
  }, [prescriber, updateFormData])

  useEffect(() => {
    const isNpiValid = validateNpi(prescriberFormData.npi)
    setIsValidNpi(isNpiValid)
  }, [prescriberFormData.npi])

  const selectedGenderOption = useMemo(() => findGenderOption(prescriberFormData.gender), [prescriberFormData.gender])

  return (
    <>
      <RXDataRow>
        <ListRowLabel>
          <CompoundLabel>First Name:</CompoundLabel>
          <CompoundLabel>Last Name:</CompoundLabel>
        </ListRowLabel>
        <CompoundValue>
          <CompoundTextInput
            data-private
            data-testid="firstName"
            value={prescriberFormData?.firstName ?? ''}
            onChange={e => {
              updateFormData({
                prescriber: {
                  firstName: { $set: e.target.value },
                },
              })
            }}
          />
          <CompoundTextInput
            data-private
            data-testid="lastName"
            value={prescriberFormData?.lastName ?? ''}
            onChange={e => {
              updateFormData({
                prescriber: {
                  lastName: { $set: e.target.value },
                },
              })
            }}
          />
        </CompoundValue>
      </RXDataRow>
      {isVet && prescriberFormData?.npi ? (
        <RXDataRow>
          <ListRowLabel>NPI:</ListRowLabel>
          <NpiTextInput
            data-private
            data-testid="npi"
            value={prescriberFormData?.npi ?? ''}
            hasError={!isValidNpi && !!prescriberFormData?.npi}
            onChange={e => {
              updateFormData({
                prescriber: {
                  npi: { $set: e.target.value },
                },
              })
            }}
          />
        </RXDataRow>
      ) : (
        ''
      )}
      <RXDataRow>
        <ListRowLabel>{isVet ? 'License #:' : 'NPI:'}</ListRowLabel>
        {isVet ? (
          <CompoundTextInput
            data-private
            data-testid="stateLicense"
            value={prescriberFormData?.stateLicense ?? ''}
            onChange={e => {
              updateFormData({
                prescriber: {
                  stateLicense: { $set: e.target.value },
                },
              })
            }}
          />
        ) : (
          <NpiTextInput
            data-private
            data-testid="npi"
            value={prescriberFormData?.npi ?? ''}
            hasError={!isValidNpi && !!prescriberFormData?.npi}
            onChange={e => {
              updateFormData({
                prescriber: {
                  npi: { $set: e.target.value },
                },
              })
            }}
          />
        )}
      </RXDataRow>
      {isVet ? (
        ''
      ) : (
        <RXDataRow>
          <ListRowLabel>Gender:</ListRowLabel>
          <StyledSelect
            data-testid="gender"
            value={selectedGenderOption}
            disableClear
            options={options}
            onChange={([option]) => {
              updateFormData({
                prescriber: {
                  gender: { $set: option?.value },
                },
              })
            }}
          />
        </RXDataRow>
      )}
      <RXDataRow>
        <ListRowLabel>Credential:</ListRowLabel>
        <CompoundTextInput
          data-private
          data-testid="suffix"
          value={prescriberFormData?.suffix ?? ''}
          onChange={e => {
            updateFormData({
              prescriber: {
                suffix: { $set: e.target.value },
              },
            })
          }}
        />
      </RXDataRow>
      <RXDataRow>
        <ListRowLabel>DEA:</ListRowLabel>
        <CompoundTextInput
          data-private
          data-testid="dea"
          value={prescriberFormData?.dea ?? ''}
          onChange={e => {
            updateFormData({
              prescriber: {
                dea: { $set: e.target.value },
              },
            })
          }}
        />
      </RXDataRow>
    </>
  )
}

export default EditDemographicBlock
